import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import DataFileMap from '@/model/Resource/DataFileMap'
import type { NewProjectResolution } from '@/model/buildDefaultMainProject'
import { buildDefaultVirtualResourceRoot, syncVirtualResourcePathMetadata } from '@/features/virtual-resource/rules'
import {
  getLocalStaticResourceMap,
  listLocalStaticResourceMaps,
  type LocalStaticResourceItem,
} from '@/features/static-resource/localResourceMap'

type VirtualRoots = Record<ResourceCategoryKey, DataFileMap | null>
const BUILTIN_SOURCE_LINK_INDEX = 1

function buildBuiltinResourceMap(): Map<string, { url: string; md5: string; size: number }> {
  const out = new Map<string, { url: string; md5: string; size: number }>()
  for (const resourceMap of listLocalStaticResourceMaps()) {
    for (const item of [...resourceMap.rtp.graphic, ...resourceMap.rtp.audio]) {
      const sourceKey = item.sourceKey.trim()
      const url = item.url.trim()
      if (!sourceKey || !url) continue
      out.set(sourceKey, { url, md5: item.md5, size: item.size })
    }
  }
  return out
}

function refreshRootBuiltInUrls(root: DataFileMap | null, resourceMap: Map<string, { url: string; md5: string; size: number }>): DataFileMap | null {
  if (!root) return null
  function walk(node: DataFileMap): void {
    if (!node.isFolder) {
      const sourceUrl = node.links[BUILTIN_SOURCE_LINK_INDEX]?.trim() ?? ''
      const official = resourceMap.get(sourceUrl)
      if (official) {
        if (node.resourceUrl !== official.url) node.resourceUrl = official.url
        node.resourceMd5 = official.md5
        node.resourceSize = official.size
      }
      return
    }
    for (const child of node.children) walk(child)
  }
  walk(root)
  return root
}

/** 将已保存工程中的内置 RTP 资源 URL 刷新为当前映射表指定的分支资源地址。 */
export function refreshBuiltInVirtualResourceUrls(roots: VirtualRoots): VirtualRoots {
  const resourceMap = buildBuiltinResourceMap()
  return {
    audio: refreshRootBuiltInUrls(roots.audio, resourceMap),
    graphic: refreshRootBuiltInUrls(roots.graphic, resourceMap),
    font: roots.font,
    video: roots.video,
  }
}

function ensureRoot(category: ResourceCategoryKey, root: DataFileMap | null): DataFileMap {
  return root ?? buildDefaultVirtualResourceRoot(category)
}

function ensureFolder(root: DataFileMap, segments: string[]): DataFileMap {
  let current = root
  for (const segment of segments.filter(Boolean)) {
    let next = current.children.find((item) => item.isFolder && item.name === segment) ?? null
    if (!next) {
      next = DataFileMap.categoryRoot(segment)
      current.children.push(next)
    }
    current = next
  }
  return current
}

function appendBuiltInResource(root: DataFileMap, item: LocalStaticResourceItem): void {
  const folder = ensureFolder(root, item.folderSegments)
  const existing = folder.children.find((child) =>
    !child.isFolder && (
      child.links[BUILTIN_SOURCE_LINK_INDEX]?.trim() === item.sourceKey
      || (child.name === item.fileName && child.resourceUrl === item.url)
    ),
  )
  const leaf = existing ?? DataFileMap.fileLeaf(item.fileName, item.url)
  leaf.links[BUILTIN_SOURCE_LINK_INDEX] = item.sourceKey
  leaf.resourceMd5 = item.md5
  leaf.resourceSize = item.size
  if (!existing) folder.children.push(leaf)
}

function appendBuiltInResources(
  category: ResourceCategoryKey,
  root: DataFileMap | null,
  items: LocalStaticResourceItem[],
): DataFileMap {
  const nextRoot = ensureRoot(category, root)
  for (const item of items) {
    if (!item.url) continue
    appendBuiltInResource(nextRoot, item)
  }
  return syncVirtualResourcePathMetadata(category, nextRoot)
}

/**
 * 批量写入分辨率对应的内置 RTP 资源树。
 *
 * 新建工程会一次性追加所有内置资源，再按分类同步路径元数据，避免逐资源克隆整棵树导致主线程卡顿。
 */
export function seedBuiltInVirtualResources(params: {
  resolution: NewProjectResolution
  roots: VirtualRoots
}): VirtualRoots {
  const nextRoots: VirtualRoots = {
    graphic: params.roots.graphic,
    audio: params.roots.audio,
    font: params.roots.font,
    video: params.roots.video,
  }
  const resourceMap = getLocalStaticResourceMap(params.resolution)
  if (!resourceMap) return nextRoots

  nextRoots.graphic = appendBuiltInResources('graphic', nextRoots.graphic, resourceMap.rtp.graphic)
  nextRoots.audio = appendBuiltInResources('audio', nextRoots.audio, resourceMap.rtp.audio)

  return nextRoots
}
